import React, { FC, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@pd/pd-ui-kit";

import {
  DealerReportStatus,
  useSetDealerReportAccountingMutation,
} from "~/schemas/generated/graphql";

import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";

import { AccountingColumnsInfo_pril1, STATUS } from "./options";

import s from "./AccountingTable.less";
import Preloader from "~/components/Preloader";

import Button from "~/shared/ui/Fields/Button";
import Modal from "~/shared/ui/Fields/Modal";

import TextArea from "~/shared/ui/Fields/TextArea";
import { useForm } from "react-hook-form/dist/index.ie11";
import { getFormatDate } from "~/libs/getFormatDate";
import { TableV2 } from "~/shared/ui/TableV2";
import { ColumnsType } from "antd/es/table";
import { usePermissions } from "~/shared/context/PermissionContext";

interface IProps {
  accountingsData;
  loading;
  refetch;
}

type ValueCol = { label: string; value: number };

type AccountingRow = {
  name: string;
  code: string;
  count: boolean;
  ReportId: string;
  nameColumn: ValueCol;
  nameColumn2?: ValueCol;
  nameColumn3?: ValueCol;
  id?: string;
};

const AccountingTable: FC<IProps> = ({ accountingsData, refetch, loading }) => {
  const { t } = useTranslation();
  const { searchField, sortType, openModal } = useGetLocationData();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const setData = useSetLocationData();
  const { control, handleSubmit } = useForm();

  const canCheck = hasPermission("accounting.setDealerReport.check");

  const canConfirm = hasPermission("accounting.setDealerReport.confirm");
  const handleCloseModal = () => {
    setData("openModal", "");
  };

  const prettifys = (num: number) => new Intl.NumberFormat().format(num);

  const [changeReport] = useSetDealerReportAccountingMutation({
    onCompleted() {
      refetch();
      handleCloseModal();
    },
  });

  const result = useMemo(() => {
    const sorted = [...accountingsData].sort((a, b) =>
      a.ReportPeriod.Year !== b.ReportPeriod.Year
        ? b.ReportPeriod.Year - a.ReportPeriod.Year
        : b.ReportPeriod.Quarter - a.ReportPeriod.Quarter,
    );

    return sorted
      .filter((el, index) =>
        index === 0
          ? true
          : sortType === "form2"
          ? el.ReportPeriod.Quarter === sorted[0]?.ReportPeriod?.Quarter
          : el.ReportPeriod.Quarter === 4,
      )
      .slice(0, 3);
  }, [accountingsData, sortType]);

  const TABLE_OPTIONS =
    result.length === 3 && sortType !== "form2"
      ? ["name", "code", "nameColumn", "nameColumn2", "nameColumn3"]
      : result.length === 2 || (result.length > 1 && sortType === "form2")
      ? ["name", "code", "nameColumn", "nameColumn2"]
      : ["name", "code", "nameColumn"];

  const nameColumnArr =
    result.length === 3
      ? ["nameColumn", "nameColumn2", "nameColumn3"]
      : result.length === 2
      ? ["nameColumn", "nameColumn2"]
      : ["nameColumn"];

  const handleOpenModal = () => {
    setData("openModal", "statusNo");
  };

  const accountingsFull: AccountingRow[] = !loading
    ? AccountingColumnsInfo_pril1[
        !!searchField ? searchField : "appendix_1"
      ].map((elem) => {
        const obj: AccountingRow = {
          name: elem.name,
          code: elem.code,
          count: elem.count,
          ReportId: result[0]["id"],
          nameColumn: {
            label: elem.nameColumn,
            value: result[0][elem.nameColumn],
          },
        };

        if (result.length === 2) {
          obj["nameColumn2"] = {
            label: elem.nameColumn,
            value: result[1][elem.nameColumn],
          };
        }
        if (result.length === 3) {
          obj["nameColumn2"] = {
            label: elem.nameColumn,
            value: result[1][elem.nameColumn],
          };
          obj["nameColumn3"] = {
            label: elem.nameColumn,
            value: result[2][elem.nameColumn],
          };
        }

        return obj;
      })
    : [];

  const onChangeInput = useCallback(
    (name, reportId, value) => {
      const obj: Record<string, number> = {};
      obj[name] = Number(value);

      changeReport({
        variables: {
          ReportId: reportId,
          ...obj,
        },
      });
    },
    [changeReport]
  );

  const onChangeStatusDealer = () => {
    changeReport({
      variables: {
        ReportId: result[0]["id"],
        status: DealerReportStatus["Checking"],
      },
    });
  };

  const onChangeStatusYes = () => {
    changeReport({
      variables: {
        ReportId: result[0]["id"],
        note: "",
        status: DealerReportStatus["Verified"],
      },
    });
  };

  const onChangeStatusNo = (data) => {
    changeReport({
      variables: {
        ReportId: result[0]["id"],
        note: data["note"],
        status: DealerReportStatus["Fixing"],
      },
    });
  };

  const formattedDates = result.map(
    (item) => getFormatDate(item.ReportPeriod?.reportDate, true) + ", тыс. руб"
  );

  const renderValueCell = useCallback(
    (record: AccountingRow, optionName: string) => {
      if (
        optionName === "nameColumn" &&
        !record.count &&
        canCheck &&
        STATUS.includes(result[0]?.["status"])
      ) {
        return (
          <Test
            item={record}
            optionName="nameColumn"
            onChangeInput={onChangeInput}
          />
        );
      }
      if (nameColumnArr.includes(optionName)) {
        const col = record[optionName as keyof AccountingRow] as ValueCol;
        return prettifys(col.value);
      }
      return null;
    },
    [canCheck, nameColumnArr, onChangeInput, result]
  );

  const columns: ColumnsType<AccountingRow> = useMemo(() => {
    const valueKeys = TABLE_OPTIONS.filter((k) => k !== "name" && k !== "code");
    const cols: ColumnsType<AccountingRow> = [
      {
        title: t("accounting.name"),
        dataIndex: "name",
        key: "name",
        width: 300,
        className: s.cellName,
      },
      {
        title: t("accounting.code"),
        dataIndex: "code",
        key: "code",
        width: 90,
      },
    ];
    valueKeys.forEach((el) => {
      const dateIdx = el === "nameColumn" ? 0 : el === "nameColumn2" ? 1 : 2;
      cols.push({
        title: (
          <span>
            {t(`accounting.${el}`)} {formattedDates[dateIdx] ?? ""}
          </span>
        ),
        key: el,
        align: "right",
        className: s.cellValue,
        render: (_: unknown, record: AccountingRow) =>
          renderValueCell(record, el),
      });
    });
    return cols;
  }, [TABLE_OPTIONS, t, formattedDates, renderValueCell]);

  return (
    <>
      {loading ? (
        <Preloader />
      ) : (
        <>
          <div className={s.allBtn}>
            {canCheck &&
              STATUS.includes(result[0]["status"]) &&
              result[0]["state"] === "good" &&
              sortType === "form2" && (
                <Button
                  small
                  txtBtn={t(`accounting.checkingReport`)}
                  onClick={onChangeStatusDealer}
                />
              )}
            {canConfirm &&
              result[0]["status"] === "checking" &&
              sortType === "form2" && (
                <>
                  <Button
                    small
                    txtBtn={t(`accounting.verifiedReport`)}
                    onClick={onChangeStatusYes}
                  />
                </>
              )}
            {canConfirm &&
              (result[0]["status"] === "verified" ||
                result[0]["status"] === "checking") &&
              sortType === "form2" && (
                <>
                  <Button
                    small
                    txtBtn={t(`accounting.verifiedReportNot`)}
                    onClick={handleOpenModal}
                  />
                </>
              )}
          </div>

          {sortType === "form2" && result[0]["state"] === "formOneZero" && (
            <div className={s.errorWrapper}>
              Форма 1 не заполнена, отправка на согласование заблокирована
            </div>
          )}

          {sortType === "form2" && result[0]["state"] === "formTwoZero" && (
            <div className={s.errorWrapper}>
              Форма 2 не заполнена, отправка на согласование заблокирована
            </div>
          )}
          {sortType === "form2" && result[0]["state"] === "noFile" && (
            <div className={s.errorWrapper}>
              Отсутствуют файлы, отправка на согласование заблокирована
            </div>
          )}

          {sortType === "form2" && result[0]["state"] === "accountingBad" && (
            <div className={s.errorWrapper}>
              Нарушено бухгалтерское равенство
            </div>
          )}
          <TableV2<AccountingRow>
            columns={columns}
            dataSource={accountingsFull}
            rowKey="code"
            pagination={false}
            scroll={{ x: "max-content" }}
            rowDecorator={(record) =>
              record.count ? { className: s.back_orange } : {}
            }
          />
        </>
      )}

      <Modal
        isOpen={openModal === "statusNo"}
        title={t(`accounting.fixingReport`)}
        actionClose={handleCloseModal}
      >
        <form onSubmit={handleSubmit(onChangeStatusNo)}>
          <div className={s.formReport}>
            <TextArea
              name={"note"}
              label={t(`accounting.fixingReportNote`)}
              control={control}
              customClassName={s.select_width}
            />
            <Button txtBtn={t("ui.save")} isForm />
          </div>
        </form>
      </Modal>
    </>
  );
};

export default AccountingTable;

const Test = ({ item, optionName, onChangeInput }) => {
  const [valueInput, setValueInput] = useState(item["nameColumn"].value);

  const stringLength = 9;
  const maxLength =
    stringLength % 3 === 0
      ? stringLength + (Math.floor(stringLength / 3) - 1)
      : stringLength % 3 !== 0
      ? stringLength + Math.floor(stringLength / 3)
      : null;

  const prettify = (str = "") =>
    String(str).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");

  return (
    <Input
      name={item[optionName].label}
      type={"text"}
      size="small"
      value={prettify(valueInput)}
      className={s.textRight}
      onChange={(e) =>
        setValueInput((prevState) => {
          if (e.target.value.match(/^[-]?[0-9,\s]*\d{0,2}$/g)) {
            return e.target.value[0] === "0"
              ? prettify(e.target.value.replace(/\s/g, "")).replace("0", "")
              : prettify(e.target.value.replace(/\s/g, ""));
          }

          return prevState;
        })
      }
      maxLength={maxLength}
      onBlur={(event) => {
        if (event.target.value === "") {
          setValueInput(0);
        }
        onChangeInput(
          item["nameColumn"].label,
          item["ReportId"],
          event.target.value.replace(/\s/g, "")
        );
      }}
    />
  );
};
